import React, { useEffect, useRef, useState } from 'react';
import Icone from '../primitivos/Icone';

/**
 * Campo de busca com debounce (at-56, frente B).
 *
 * O debounce de **400ms** é o padrão do projeto desde a at-10, e estava copiado à
 * mão em `/adesoes`, `/ti/envios`, `/carteira`, `/vistoria` e `/ti/bikes` — cinco
 * `useEffect` idênticos com o mesmo `setTimeout`. Aqui ele é do componente.
 *
 * 🔴 **`type="search"` NÃO é detalhe — é o que evita um bug silencioso.**
 * `ui/tokens/legado-compartilhado.css` declara **`input[type="text"]` como
 * seletor de elemento**, ou seja, global para o app inteiro (o CRA concatena todo
 * CSS importado num bundle único). E ele **vence** os utilitários do Tailwind por
 * especificidade — `input[type="text"]` é (0,1,1) contra (0,1,0) de uma classe —,
 * então um `<input type="text">` estilizado só com `tw-` sairia com raio de 4px e
 * borda `#ccc` do legado, **sem erro nenhum**. Aquele arquivo é importado por 19
 * telas e não pode ser apagado nesta frente (mover as regras globais dele para
 * `tokens/base.css` é trabalho da frente D, porque muda quem ganha nos empates).
 * `type="search"` fica fora do alcance do seletor, e é semanticamente correto.
 * ⚠️ **Não "corrigir" para `type="text"`.**
 *
 * ⚠️ **Os pseudo-elementos do WebKit precisam ser zerados um por um.** Com o
 * `preflight` desligado, `type="search"` herda a aparência do navegador, e
 * `appearance: none` no elemento **não** remove `::-webkit-search-decoration`
 * nem `::-webkit-search-cancel-button` — eles desenham um contorno interno e um
 * "×" nativo que fazem o campo parecer ter duas bordas. Foi o que Victor viu em
 * 09/09/2026 ("campo de busca está feio").
 *
 * ⚠️ O debounce dispara **só em resposta à digitação**, nunca na montagem — o
 * padrão antigo (`useEffect` sobre o estado) emitia uma busca vazia ao montar e
 * fazia o consumidor pedir a primeira página duas vezes.
 */

interface PropsSearchInput {
  /** Chamado com o termo já "debounced" e com `trim`. */
  aoBuscar: (termo: string) => void;
  placeholder: string;
  /** Valor com que o campo nasce. Não dispara busca. */
  valorInicial?: string;
  /** Rótulo para leitor de tela — o `placeholder` não serve como rótulo. */
  rotuloAcessivel?: string;
  disabled?: boolean;
  className?: string;
}

/** Padrão do projeto desde a at-10 — não mudar sem mudar nas outras telas. */
const ATRASO_MS = 400;

const SearchInput: React.FC<PropsSearchInput> = ({
  aoBuscar,
  placeholder,
  valorInicial = '',
  rotuloAcessivel = 'Buscar',
  disabled = false,
  className = '',
}) => {
  const [texto, setTexto] = useState(valorInicial);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Um timer pendente depois da desmontagem chamaria `aoBuscar` de uma tela que
  // já não existe (o clássico "setState on unmounted component").
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const digitar = (valor: string) => {
    setTexto(valor);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => aoBuscar(valor.trim()), ATRASO_MS);
  };

  return (
    /*
     * ⚠️ **Sem `tw-w-full` no wrapper.** Ele é uma `<div>`, então em contexto de
     * bloco já ocupa a largura toda — mas `width: 100%` num **flex item** faz o
     * campo tomar a linha inteira e empurrar o que estivesse ao lado para baixo
     * (era o checkbox "Mostrar histórico", visto por Victor em 09/09/2026).
     * Passar `flex-1` no `className` não desfazia: `flex-basis` e `width` são
     * propriedades diferentes, e o `width` continuava valendo. Quem decide a
     * largura é o consumidor.
     */
    <div className={['tw-relative', className].filter(Boolean).join(' ')}>
      <span
        className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-texto-desabilitado"
        aria-hidden="true"
      >
        <Icone nome="busca" tamanho={16} />
      </span>

      <input
        /* 🔴 "search", não "text" — ver o aviso no topo do arquivo. */
        type="search"
        value={texto}
        onChange={(evento) => digitar(evento.target.value)}
        placeholder={placeholder}
        aria-label={rotuloAcessivel}
        disabled={disabled}
        className={[
          /* Medidas do protótipo: `rounded-xl`, fundo BRANCO, `py-3 pl-9 pr-4`.
             `py-3` + `text-sm` dá 44px de altura, o alvo de toque da spec 01 §7. */
          'tw-w-full tw-appearance-none tw-rounded-xl tw-border tw-border-superficie-borda',
          'tw-bg-white tw-py-3 tw-pl-9 tw-pr-4 tw-font-corpo tw-text-sm tw-text-texto-corpo',
          'placeholder:tw-text-texto-desabilitado',
          'focus:tw-border-brand focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand/20',
          'disabled:tw-cursor-not-allowed disabled:tw-bg-superficie disabled:tw-text-texto-desabilitado',
          /* Zera os pseudo-elementos do WebKit — ver o aviso no topo do arquivo. */
          '[&::-webkit-search-decoration]:tw-appearance-none',
          '[&::-webkit-search-cancel-button]:tw-appearance-none',
          '[&::-webkit-search-results-button]:tw-appearance-none',
        ].join(' ')}
      />
    </div>
  );
};

export default SearchInput;
